import React, { useState } from 'react';
import { Minus, Plus } from 'lucide-react';

interface QuantityInputProps {
    value: number;
    onChange: (value: number) => void;
    /** Minimo selezionabile (default 1). */
    min?: number;
    /** Massimo selezionabile, es. la giacenza disponibile. */
    max?: number;
    disabled?: boolean;
    className?: string;
}

/**
 * Selettore di quantità alto quanto gli altri campi del form: si usa con i
 * bottoni +/− oppure scrivendo direttamente il numero.
 */
const QuantityInput: React.FC<QuantityInputProps> = ({
    value,
    onChange,
    min = 1,
    max,
    disabled = false,
    className = '',
}) => {
    // Mentre si scrive il campo può restare vuoto: si normalizza uscendo.
    const [draft, setDraft] = useState<string | null>(null);

    const clamp = (n: number) => Math.min(max ?? Number.MAX_SAFE_INTEGER, Math.max(min, n));

    const handleType = (raw: string) => {
        const digits = raw.replace(/\D/g, '');
        setDraft(digits);
        if (digits !== '') onChange(clamp(parseInt(digits, 10)));
    };

    const handleBlur = () => {
        if (draft === null) return;
        onChange(draft === '' ? min : clamp(parseInt(draft, 10)));
        setDraft(null);
    };

    const step = (delta: number) => {
        setDraft(null);
        onChange(clamp(value + delta));
    };

    const canDecrease = !disabled && value > min;
    const canIncrease = !disabled && (max == null || value < max);

    return (
        <div
            className={`flex items-stretch rounded-xl border bg-gray-50 border-gray-200 overflow-hidden transition-all focus-within:bg-white focus-within:border-clip-400 focus-within:ring-2 focus-within:ring-clip-200 ${disabled ? 'opacity-50' : ''
                } ${className}`}
        >
            <button
                type="button"
                onClick={() => step(-1)}
                disabled={!canDecrease}
                aria-label="-1"
                className="px-3 flex items-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
                <Minus size={16} />
            </button>

            <input
                type="text"
                inputMode="numeric"
                value={draft ?? String(value)}
                onChange={(e) => handleType(e.target.value)}
                onBlur={handleBlur}
                onFocus={(e) => e.currentTarget.select()}
                disabled={disabled}
                className="min-w-0 flex-1 w-full py-3 text-center bg-transparent outline-none font-medium text-gray-900 tabular-nums"
            />

            <button
                type="button"
                onClick={() => step(1)}
                disabled={!canIncrease}
                aria-label="+1"
                className="px-3 flex items-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
                <Plus size={16} />
            </button>
        </div>
    );
};

export default QuantityInput;
